'use client';

import { useToast } from '@common/hooks';
import SvgShoppingBasket from '@common/icon/SvgShoppingBasket';
import SvgUserCircle from '@common/icon/SvgUserCircle';
import { cn } from '@core/utils';
import { captureException } from '@sentry/nextjs';
import { useState } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { PageNavigation } from '@/components/client';
import { ROUTES, WEL_MARKET_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import type { GtmCommerceItem } from '@/utils/analytics/gtm';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { ProductImageGallery } from '../../../_components/ProductImageGallery';
import { useMarketCart } from '../../../cart/_hooks/useMarketCart';
import type { MarketProduct, ProductOption, SelectedOption } from '../types';
import { DetailBottomBar } from './DetailBottomBar';
import { DonationGiftBanner } from './DonationGiftBanner';
import { MarketDetailContent } from './MarketDetailContent';
import { MarketDetailTab } from './MarketDetailTab';
import { ProductInfo } from './ProductInfo';
import { ReviewPreview } from './ReviewPreview';
interface MarketDetailTemplateProps {
  product: MarketProduct;
}
export const MarketDetailTemplate = ({
  product
}: MarketDetailTemplateProps) => {
  const isSoldOut = product.soldOutYN && product.salePhase === 'ON_SALE';
  const isPreparing = product.salePhase !== 'ON_SALE' || isSoldOut;
  const preparingLabel = isSoldOut ? '품절된 상품이에요' : product.salePhase === 'SALE_ENDED' ? '판매 종료' : '상품 준비중이에요';
  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);
  const handleSelectOption = (option: ProductOption) => {
    setSelectedOptions(prev => {
      const exists = prev.find(o => o.optionId === option.id);
      if (exists) {
        return prev.map(o_0 => o_0.optionId === option.id ? {
          ...o_0,
          quantity: o_0.quantity + 1
        } : o_0);
      }
      return [...prev, {
        optionId: option.id,
        name: option.name,
        price: option.price,
        quantity: 1
      }];
    });
  };
  const handleQuantityChange = (optionId: string, quantity: number) => {
    if (quantity < 1) {
      setSelectedOptions(prev_0 => prev_0.filter(o_1 => o_1.optionId !== optionId));
      return;
    }
    setSelectedOptions(prev_1 => prev_1.map(o_2 => o_2.optionId === optionId ? {
      ...o_2,
      quantity
    } : o_2));
  };
  const handleRemoveOption = (optionId_0: string) => {
    setSelectedOptions(prev_2 => prev_2.filter(o_3 => o_3.optionId !== optionId_0));
  };
  const {
    toast
  } = useToast();
  const {
    addItem,
    isAdding,
    totalCount
  } = useMarketCart();
  const router = useCustomRouter();
  const buildCommerceItems = (totalQuantity: number): GtmCommerceItem[] => [{
    item_type: '웰마켙상품',
    price: product.donorPrice,
    item_name: product.productName,
    quantity: totalQuantity,
    item_id: product.productId,
    product_uuid: product.productId,
    original_price: product.regularPrice,
    discount_rate: product.discountRate,
    product_brand_name: product.vendorName,
    is_donation_linked: product.giftProductNumber ? 'Y' : 'N',
    ...(product.giftProductNumber ? {
      linked_gift_id: product.giftProductNumber
    } : {})
  }];
  const handleBuyNow = async () => {
    if (selectedOptions.length === 0) return;
    trackGtmEvent(GTM_EVENT_TYPE.START_PURCHASE, {
      currency: 'KRW',
      value: selectedOptions.reduce((sum, option_0) => sum + option_0.price * option_0.quantity, 0),
      local_name: product.govCodeName,
      items: buildCommerceItems(selectedOptions.reduce((sum_0, option_1) => sum_0 + option_1.quantity, 0))
    });
    try {
      const addResult = await addItem({
        mallProductId: product.mallProductId,
        mallVendorId: product.mallVendorId,
        options: selectedOptions.map(o_4 => ({
          productOptionItemId: o_4.optionId,
          quantity: o_4.quantity
        })),
        cartType: 'INSTANT'
      });
      const beforeOrderId = addResult?.context?.beforeOrderId;
      if (!beforeOrderId) {
        toast({
          message: '주문 처리 중 오류가 발생했어요.',
          type: 'fail'
        });
        return;
      }
      setSelectedOptions([]);
      router.push(WEL_MARKET_ROUTES.ORDER.pathname({
        beforeOrderId
      }));
    } catch {
      toast({
        message: '주문 처리에 실패했어요. 다시 시도해주세요.',
        type: 'fail'
      });
    }
  };
  const handleAddToCart = async () => {
    if (selectedOptions.length === 0) return;
    try {
      await addItem({
        mallProductId: product.mallProductId,
        mallVendorId: product.mallVendorId,
        options: selectedOptions.map(o_5 => ({
          productOptionItemId: o_5.optionId,
          quantity: o_5.quantity
        })),
        cartType: 'ON_CART'
      });
      trackGtmEvent(GTM_EVENT_TYPE.ADD_TO_CART, {
        currency: 'KRW',
        value: selectedOptions.reduce((sum_1, option_2) => sum_1 + option_2.price * option_2.quantity, 0),
        local_name: product.govCodeName,
        items: buildCommerceItems(selectedOptions.reduce((sum_2, option_3) => sum_2 + option_3.quantity, 0))
      });
      toast({
        message: '장바구니에 담았어요'
      });
      setSelectedOptions([]);
    } catch (error) {
      captureException(error);
      toast({
        message: '장바구니 담기에 실패했어요',
        type: 'fail'
      });
    }
  };
  const totalAmount = selectedOptions.reduce((sum_3, o_6) => sum_3 + o_6.price * o_6.quantity, 0);
  return <>
      <div className={cn('flex gap-60', 'mobile-tablet:flex-col mobile-tablet:gap-0')}>
        <PageNavigation title="상품 상세페이지" isNotUsedHomeButton>
          <div className="flex items-center gap-20">
            <CustomLink href={WEL_MARKET_ROUTES.CART.pathname} aria-label="장바구니" className="relative size-26">
              <SvgShoppingBasket className="size-24" />
              {totalCount > 0 && <span className="absolute -right-6 -top-4 flex size-16 items-center justify-center rounded-pill bg-O-400 font-600 text-Gray-white typo-caption2">
                  {totalCount > 99 ? '99+' : totalCount}
                </span>}
            </CustomLink>
            <CustomLink href={ROUTES.MY.pathname} aria-label="마이페이지">
              <SvgUserCircle className="size-24 text-Gray-900" />
            </CustomLink>
          </div>
        </PageNavigation>
        {/* Left: Main content column */}
        <div className={cn('flex min-w-0 flex-1 flex-col gap-56 pt-40', 'mobile-tablet:gap-12 mobile-tablet:pt-0')}>
          {/* Info 영역 (모바일 gap-20) */}
          <ProductImageGallery mainImageUrl={product.mainImageUrl} subImageUrlList={product.subImageUrlList} socialProofCount={product.socialProofCount} isPreparing={isPreparing} />
          {/* Mobile only: ProductInfo appears between image and reviews */}
          <div className="hidden mobile-tablet:block">
            <ProductInfo product={product} productId={product.productId} selectedOptions={selectedOptions} totalAmount={totalAmount} onSelectOption={handleSelectOption} onQuantityChange={handleQuantityChange} onRemoveOption={handleRemoveOption} onBuyNow={handleBuyNow} isBuyingNow={isAdding} isPreparing={isPreparing} preparingLabel={preparingLabel} />
          </div>

          {/* @Contents 영역 (모바일 gap-32) */}
          <div className={cn('flex flex-col gap-48', 'mobile-tablet:gap-32 mobile-tablet:pt-20')}>
            <ReviewPreview productId={product.productId} />
            <DonationGiftBanner product={product} className="hidden mobile-tablet:flex" />

            {/* Tab Navigation */}
            <MarketDetailTab className="sticky top-[72px] z-10 mt-32 bg-Gray-white shadow-[0_-40px_0_0_white] mobile-tablet:top-[48px] mobile-tablet:mt-0 mobile-tablet:shadow-none" />

            {/* Content */}
            <MarketDetailContent productInfo={product.productInfo} productInfoSections={product.productInfoSections} vendorName={product.vendorName} vendorContactNumber={product.vendorContactNumber} deliveryFee={product.deliveryFee} deliveryCompanyName={product.deliveryCompanyName} productId={product.productId} productImageUrl={product.mainImageUrl ?? undefined} vendorRegion={product.govCodeName} productName={product.productName} />
          </div>
        </div>

        {/* Right: Product Info sidebar - Desktop only (sticky) */}
        <div className={cn('w-[30rem] shrink-0', 'sticky top-0 self-start', 'max-h-vh overflow-y-auto pb-200 pt-0 hide-scrollbar', 'mobile-tablet:hidden')}>
          <ProductInfo product={product} productId={product.productId} selectedOptions={selectedOptions} totalAmount={totalAmount} onSelectOption={handleSelectOption} onQuantityChange={handleQuantityChange} onRemoveOption={handleRemoveOption} onAddToCart={handleAddToCart} isAddingToCart={isAdding} onBuyNow={handleBuyNow} isBuyingNow={isAdding} isPreparing={isPreparing} preparingLabel={preparingLabel} />
        </div>
      </div>

      {/* Mobile fixed bottom bar */}
      <DetailBottomBar totalAmount={totalAmount} options={product.options} selectedOptions={selectedOptions} onSelectOption={handleSelectOption} onQuantityChange={handleQuantityChange} onRemoveOption={handleRemoveOption} onAddToCart={handleAddToCart} isAddingToCart={isAdding} onBuyNow={handleBuyNow} isBuyingNow={isAdding} socialProofCount={product.socialProofCount} isPreparing={isPreparing} preparingLabel={preparingLabel} />
    </>;
};